import ctncProvidersData from "@/configuration/groups/nira/rules/live/ctnc-providers.json";
import fortCollinsProvidersData from "@/configuration/groups/nira/rules/live/fort-collins-providers.json";
import loneTreeProvidersData from "@/configuration/groups/nira/rules/live/lone-tree-providers.json";
import msnProvidersData from "@/configuration/groups/nira/rules/live/msn-providers.json";
import rmmscProvidersData from "@/configuration/groups/nira/rules/live/rmmsc-providers.json";
import { type AutoIntakeConfig } from "@/src/lib/auto-intake/auto-intake-config";
import { SlotPostProcessOption } from "@/src/lib/ehr/scheduling/slot-post-process";
import { logAndThrow } from "@/src/lib/logging/logger";
import { Tenancy } from "@/src/lib/tenancy";
import type { ProviderInfo } from "@/src/lib/types/provider.types";
import {
  extractPractitionerNPIs,
  extractProviderICD10Map,
  getProvidersForDiagnosis,
  type ProviderICD10Rules,
  type ProvidersConfig,
} from "@/src/lib/utils/diagnosis-based-provider-matcher";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { AppointmentType } from "@/src/models/appointment";
import { Classification, type ClassificationSection } from "@/src/models/constants/classifications";
import type { Diagnosis } from "@/src/models/documents/referral";

export enum DepartmentCluster {
  CTNC = "ctnc",
  FORT_COLLINS = "fort_collins",
  MSN = "msn",
  RMMSC = "rmmsc",
  LONE_TREE = "lone_tree",
}

const CTNC_DEPARTMENT_IDS = ["1"];
const FORT_COLLINS_DEPARTMENT_IDS = ["23"];
const MSN_DEPARTMENT_IDS = ["17", "18", "20"];
const RMMSC_DEPARTMENT_IDS = ["6", "7", "8", "10", "11", "12"];
const LONE_TREE_DEPARTMENT_IDS = ["28"];

const ctncProviders = ctncProvidersData as ProvidersConfig;
const fortCollinsProviders = fortCollinsProvidersData as ProvidersConfig;
const msnProviders = msnProvidersData as ProvidersConfig;
const rmmscProviders = rmmscProvidersData as ProvidersConfig;
const loneTreeProviders = loneTreeProvidersData as ProvidersConfig;

const CTNC_PRACTITIONER_NPIS = extractPractitionerNPIs(ctncProviders);
const CTNC_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(ctncProviders);
const CTNC_DEFAULT_PROVIDER: ProviderInfo = { name: "Lenka Svedova", ehrId: "23" };

const FORT_COLLINS_PRACTITIONER_NPIS = extractPractitionerNPIs(fortCollinsProviders);
const FORT_COLLINS_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(fortCollinsProviders);
const FORT_COLLINS_DEFAULT_PROVIDER: ProviderInfo = { name: "Sarah Yang", ehrId: "119" };

const MSN_PRACTITIONER_NPIS = extractPractitionerNPIs(msnProviders);
const MSN_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(msnProviders);
const MSN_DEFAULT_PROVIDER: ProviderInfo = { name: "Dr. Jean-Raphael Schneider", ehrId: "92" };

const RMMSC_PRACTITIONER_NPIS = extractPractitionerNPIs(rmmscProviders);
const RMMSC_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(rmmscProviders);
const RMMSC_DEFAULT_PROVIDER: ProviderInfo = { name: "DR. Viktoria Kaplan", ehrId: "2" };

const LONE_TREE_PRACTITIONER_NPIS = extractPractitionerNPIs(loneTreeProviders);
const LONE_TREE_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(loneTreeProviders);
const LONE_TREE_DEFAULT_PROVIDER: ProviderInfo = { name: "Beverly Gilder, MD", ehrId: "143" };

export function getDepartmentCluster(departmentId: string): DepartmentCluster | null {
  if (CTNC_DEPARTMENT_IDS.includes(departmentId)) return DepartmentCluster.CTNC;
  if (FORT_COLLINS_DEPARTMENT_IDS.includes(departmentId)) return DepartmentCluster.FORT_COLLINS;
  if (MSN_DEPARTMENT_IDS.includes(departmentId)) return DepartmentCluster.MSN;
  if (RMMSC_DEPARTMENT_IDS.includes(departmentId)) return DepartmentCluster.RMMSC;
  if (LONE_TREE_DEPARTMENT_IDS.includes(departmentId)) return DepartmentCluster.LONE_TREE;

  return null;
}

const NIRA_ALL_DEPARTMENT_IDS = [
  ...CTNC_DEPARTMENT_IDS,
  ...FORT_COLLINS_DEPARTMENT_IDS,
  ...MSN_DEPARTMENT_IDS,
  ...RMMSC_DEPARTMENT_IDS,
  ...LONE_TREE_DEPARTMENT_IDS,
];

export const NIRA_DEPARTMENT_NAMES: Record<string, string> = {
  "1": "Central Texas Neurology Consultants",
  "6": "RMMSC Clinic SLC",
  "7": "RMMSC Infusion SLC",
  "8": "RMMSC Research SLC",
  "10": "RMMSC Clinic Lehi",
  "11": "RMMSC Infusion Lehi",
  "12": "RMMSC Research Lehi",
  "17": "MSN Clinic Clearwater",
  "18": "MSN Infusion Clearwater",
  "20": "MSN Hospital Clearwater",
  "23": "ANC Clinic Fort Collins",
  "28": "ANC Clinic Lone Tree",
};

export function getNiraDepartmentIds(): string[] {
  return NIRA_ALL_DEPARTMENT_IDS;
}

export function getNiraDepartmentName(departmentId: string): string | null {
  return NIRA_DEPARTMENT_NAMES[departmentId] ?? null;
}

export function getNiraProvidersForDiagnosis(diagnosisList: Diagnosis[], departmentId?: string): ProviderInfo[] {
  const cluster = departmentId ? getDepartmentCluster(departmentId) : null;

  if (!cluster) {
    return [];
  }

  switch (cluster) {
    case DepartmentCluster.FORT_COLLINS:
      return getProvidersForDiagnosis(
        diagnosisList,
        FORT_COLLINS_ICD10_MAP,
        FORT_COLLINS_PRACTITIONER_NPIS,
        FORT_COLLINS_DEFAULT_PROVIDER
      );
    case DepartmentCluster.MSN:
      return getProvidersForDiagnosis(diagnosisList, MSN_ICD10_MAP, MSN_PRACTITIONER_NPIS, MSN_DEFAULT_PROVIDER);
    case DepartmentCluster.RMMSC:
      return getProvidersForDiagnosis(diagnosisList, RMMSC_ICD10_MAP, RMMSC_PRACTITIONER_NPIS, RMMSC_DEFAULT_PROVIDER);
    case DepartmentCluster.LONE_TREE:
      return getProvidersForDiagnosis(
        diagnosisList,
        LONE_TREE_ICD10_MAP,
        LONE_TREE_PRACTITIONER_NPIS,
        LONE_TREE_DEFAULT_PROVIDER
      );
    case DepartmentCluster.CTNC:
      return getProvidersForDiagnosis(diagnosisList, CTNC_ICD10_MAP, CTNC_PRACTITIONER_NPIS, CTNC_DEFAULT_PROVIDER);
  }
}

export function getNiraDefaultProviderForDepartment(departmentId: string): ProviderInfo {
  const cluster = getDepartmentCluster(departmentId);

  switch (cluster) {
    case DepartmentCluster.FORT_COLLINS:
      return FORT_COLLINS_DEFAULT_PROVIDER;
    case DepartmentCluster.MSN:
      return MSN_DEFAULT_PROVIDER;
    case DepartmentCluster.RMMSC:
      return RMMSC_DEFAULT_PROVIDER;
    case DepartmentCluster.LONE_TREE:
      return LONE_TREE_DEFAULT_PROVIDER;
    case DepartmentCluster.CTNC:
    default:
      return CTNC_DEFAULT_PROVIDER;
  }
}

const niraClassificationsForDisplay = [
  Classification.ADMIN_LEAD_FORM,
  Classification.BLANK,
  Classification.CARE_PLAN,
  Classification.CONSENT,
  Classification.COVER_PAGE,
  Classification.DME_ORDER,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.INSURANCE_CARD,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LEGAL,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.PHARMACY_RELATED,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PROCEDURE_INVENTORY_LIST,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_STATUS_REQUEST,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
  Classification.UNKNOWN,
];

const NIRA_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.PRESIDENTS_DAY,
  HolidayNames.MEMORIAL_DAY,
  HolidayNames.INDEPENDENCE_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.CHRISTMAS_DAY,
] as const;

export function getNiraHolidays(): HolidayNames[] {
  return [...NIRA_HOLIDAYS];
}

export function getNiraClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [{ label: "Classifications", options: niraClassificationsForDisplay }];
}

export function getNiraGroupingClassification(classifications: Classification[]): Classification | null {
  const referralCount = classifications.filter((c) => c === Classification.REFERRAL).length;

  if (referralCount === 1) {
    return Classification.REFERRAL;
  }

  return null;
}

export function getNiraClassificationToExtract(): Classification[] {
  return [Classification.REFERRAL];
}

export function getNiraScheduleConfigPath(tenancy: Tenancy): string {
  return tenancy == Tenancy.LIVE
    ? "configuration/groups/nira/rules/live/schedule.yaml"
    : "configuration/groups/nira/rules/sandbox/sandbox-schedule.yaml";
}

export function getValerieScheduleConfigPath(): string {
  return "configuration/groups/valerie/rules/sandbox-schedule.yaml";
}

export interface ProviderAppointmentTypeMapping {
  appointmentTypeIds: Partial<Record<AppointmentType, number>>;
  defaultAppointmentTypeId?: number;
}

type ProviderBookedAppointmentTypeReverseMapping = Partial<Record<BookedAppointmentTypeId, AppointmentType>>;

enum NiraProviderEhrIds {
  COUCH = "1",
  HORVIT = "9",
  PECKHAM = "10",
  KHOSRAVI = "18",
  SAINI = "16",
  PURDIE = "17",
  GILDER = "143",
  DASARI = "120",
  LACKEY = "121",
}

export type NiraCancellationCaseConfig = { assignee: string; departmentId: string };

// Per-provider config for the Athena patient case created when an appointment is
// cancelled via Valerie voice. Providers that are not present here opt out — no
// follow-up case is created. `assignee` is the Athena `assignedto` username;
// `departmentId` is the Athena department the case is filed under.
const NIRA_CANCELLATION_CASE_CONFIGS: Partial<Record<NiraProviderEhrIds, NiraCancellationCaseConfig>> = {
  [NiraProviderEhrIds.PECKHAM]: { assignee: "epeckham4 STAFF", departmentId: "1" },
  [NiraProviderEhrIds.COUCH]: { assignee: "ccouch28 STAFF", departmentId: "1" },
  [NiraProviderEhrIds.KHOSRAVI]: { assignee: "pkhosravi5 STAFF", departmentId: "1" },
  [NiraProviderEhrIds.PURDIE]: { assignee: "bpurdie STAFF", departmentId: "1" },
  [NiraProviderEhrIds.SAINI]: { assignee: "ksaini14 STAFF", departmentId: "1" },
};

export function getNiraCancellationCaseConfig(
  providerEhrId: string | null | undefined
): NiraCancellationCaseConfig | null {
  if (!providerEhrId) return null;

  return NIRA_CANCELLATION_CASE_CONFIGS[providerEhrId as NiraProviderEhrIds] ?? null;
}

enum OpenAppointmentTypeId {
  ANY_15 = 5,
  ANY_20 = 361,
  ANY_30 = 82,
  FOLLOW_UP_20 = 376,
  FOLLOW_UP_30 = 18,
  INJ_BOTOX_30 = 85,
  NEW_PATIENT_30 = 2,
  NEW_PATIENT_40 = 375,
  NEW_PATIENT_60 = 19,
  RESEARCH_15 = 261,
  RESEARCH_20 = 367,
  RESEARCH_30 = 262,
  TEST_EMG_30 = 14,
  TEST_EMG_45 = 87,
  URGENT_15 = 183,
  URGENT_20 = 363,
}

enum BookedAppointmentTypeId {
  FOLLOW_UP_15 = 1,
  FOLLOW_UP_20 = 376,
  FOLLOW_UP_30 = 18,
  FOLLOW_UP_40 = 377,
  FOLLOW_UP_60 = 284,
  FU_TELEHEALTH_20 = 364,
  FU_TELEHEALTH_30 = 24,
  FU_TELEPHONE_20 = 366,
  INJ_BOTOX_15 = 17,
  INJ_BOTOX_20 = 369,
  INJ_BOTOX_30 = 85,
  INJ_DYSPORT_20 = 370,
  INJ_MYOBLOC_15 = 95,
  INJ_XEOMIN_30 = 91,
  NEW_PATIENT_30 = 2,
  NEW_PATIENT_40 = 375,
  NEW_PATIENT_60 = 19,
  RESEARCH_15 = 261,
  RESEARCH_20 = 367,
  RESEARCH_30 = 262,
  PROG_DBS_30 = 264,
}

// Per-provider map from the EHR's open appointment-type id (the value carried
// on each available slot) to the booked appointment-type id Athena should
// receive when filling that slot. Some open templates (e.g. Peckham's
// ANY_20) are reused by the clinic across multiple booked categories, so each
// entry optionally carries `byIntent` overrides keyed on the agent's internal
// AppointmentType. `default` covers the dominant case and is the answer when
// no intent is supplied or no override matches.
//
// Each provider's entries should be derived from that provider's actual open +
// booked appointment data — do not extrapolate from one provider to another.
interface OpenSlotMapping {
  default: BookedAppointmentTypeId;
  byIntent?: Partial<Record<AppointmentType, BookedAppointmentTypeId>>;
}

type OpenToBookedTypeIdMapping = Partial<Record<OpenAppointmentTypeId, OpenSlotMapping>>;

const OPEN_TYPE_ID_TO_BOOKING_TYPE_ID_MAPPING: Record<NiraProviderEhrIds, OpenToBookedTypeIdMapping> = {
  [NiraProviderEhrIds.COUCH]: {
    [OpenAppointmentTypeId.NEW_PATIENT_30]: { default: BookedAppointmentTypeId.NEW_PATIENT_30 },
    [OpenAppointmentTypeId.ANY_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_15 },
    [OpenAppointmentTypeId.INJ_BOTOX_30]: { default: BookedAppointmentTypeId.INJ_BOTOX_30 },
    [OpenAppointmentTypeId.URGENT_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_15 },
  },
  [NiraProviderEhrIds.HORVIT]: {
    [OpenAppointmentTypeId.NEW_PATIENT_30]: { default: BookedAppointmentTypeId.NEW_PATIENT_30 },
    [OpenAppointmentTypeId.ANY_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_15 },
    [OpenAppointmentTypeId.URGENT_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_15 },
  },
  // PECKHAM: derived from real Peckham booked-appointment data.
  // Open templates like ANY_20 (361) are reused
  // by the clinic across follow-ups, injections, and telehealth — so the
  // dominant booked type is the default and the rest go through `byIntent`.
  [NiraProviderEhrIds.PECKHAM]: {
    [OpenAppointmentTypeId.NEW_PATIENT_40]: { default: BookedAppointmentTypeId.NEW_PATIENT_40 },
    [OpenAppointmentTypeId.NEW_PATIENT_60]: { default: BookedAppointmentTypeId.NEW_PATIENT_60 },
    [OpenAppointmentTypeId.FOLLOW_UP_20]: { default: BookedAppointmentTypeId.FOLLOW_UP_20 },
    [OpenAppointmentTypeId.ANY_20]: {
      default: BookedAppointmentTypeId.FOLLOW_UP_20,
      byIntent: {
        [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_20,
        [AppointmentType.TELEHEALTH]: BookedAppointmentTypeId.FU_TELEHEALTH_20,
      },
    },
    [OpenAppointmentTypeId.URGENT_20]: {
      default: BookedAppointmentTypeId.FOLLOW_UP_20,
      byIntent: {
        [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_20,
      },
    },
    [OpenAppointmentTypeId.RESEARCH_15]: { default: BookedAppointmentTypeId.RESEARCH_15 },
    [OpenAppointmentTypeId.RESEARCH_30]: { default: BookedAppointmentTypeId.RESEARCH_30 },
  },
  [NiraProviderEhrIds.KHOSRAVI]: {
    [OpenAppointmentTypeId.NEW_PATIENT_60]: { default: BookedAppointmentTypeId.NEW_PATIENT_60 },
    [OpenAppointmentTypeId.FOLLOW_UP_30]: { default: BookedAppointmentTypeId.FOLLOW_UP_30 },
    [OpenAppointmentTypeId.URGENT_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_30 },
  },
  [NiraProviderEhrIds.PURDIE]: {
    [OpenAppointmentTypeId.ANY_30]: { default: BookedAppointmentTypeId.FOLLOW_UP_30 },
  },
  [NiraProviderEhrIds.SAINI]: {
    [OpenAppointmentTypeId.ANY_30]: {
      default: BookedAppointmentTypeId.FOLLOW_UP_30,
      byIntent: {
        // Saini's initial consultations occupy two adjacent ANY_30 slots — see
        // the SlotMerge post-processing in src/lib/comms/scheduling/scheduling.ts.
        [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_60,
      },
    },
  },
  [NiraProviderEhrIds.GILDER]: {
    [OpenAppointmentTypeId.NEW_PATIENT_30]: { default: BookedAppointmentTypeId.NEW_PATIENT_30 },
    [OpenAppointmentTypeId.ANY_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_15 },
  },
  [NiraProviderEhrIds.DASARI]: {
    [OpenAppointmentTypeId.ANY_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_30 },
  },
  [NiraProviderEhrIds.LACKEY]: {
    [OpenAppointmentTypeId.ANY_15]: { default: BookedAppointmentTypeId.FOLLOW_UP_30 },
  },
};

export function getNiraBookedTypeIdFromOpenTypeId(
  providerEhrId: string,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number {
  const providerMapping = OPEN_TYPE_ID_TO_BOOKING_TYPE_ID_MAPPING[providerEhrId as NiraProviderEhrIds];

  if (!providerMapping) {
    logAndThrow("No open->booked appointment type mapping for NIRA provider EHR ID", { providerEhrId });
  }

  const openIdNumeric = Number(openAppointmentTypeId);
  const slotMapping = providerMapping[openIdNumeric as OpenAppointmentTypeId];

  if (slotMapping === undefined) {
    logAndThrow("No booked appointment type id for NIRA provider + open type id", {
      providerEhrId,
      openAppointmentTypeId,
    });
  }

  if (internalAppointmentType && slotMapping.byIntent?.[internalAppointmentType] !== undefined) {
    return slotMapping.byIntent[internalAppointmentType] as number;
  }

  return slotMapping.default;
}

const OPEN_APPOINTMENT_TYPE_MAPPING: Record<string, ProviderAppointmentTypeMapping> = {
  [NiraProviderEhrIds.COUCH]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.INJECTION]: OpenAppointmentTypeId.INJ_BOTOX_30,
      [AppointmentType.URGENT_CARE]: OpenAppointmentTypeId.URGENT_15,
    },
  },
  [NiraProviderEhrIds.HORVIT]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.PROCEDURE]: OpenAppointmentTypeId.TEST_EMG_30,
      [AppointmentType.URGENT_CARE]: OpenAppointmentTypeId.URGENT_15,
    },
  },
  [NiraProviderEhrIds.PECKHAM]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.NEW_PATIENT_40,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_20,
      [AppointmentType.URGENT_CARE]: OpenAppointmentTypeId.URGENT_20,
    },
  },
  [NiraProviderEhrIds.KHOSRAVI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.FOLLOW_UP_30,
      [AppointmentType.URGENT_CARE]: OpenAppointmentTypeId.URGENT_15,
    },
  },
  [NiraProviderEhrIds.PURDIE]: {
    appointmentTypeIds: {
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.INJECTION]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.PROCEDURE]: OpenAppointmentTypeId.ANY_30,
    },
  },
  [NiraProviderEhrIds.SAINI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_30,
      [AppointmentType.INJECTION]: OpenAppointmentTypeId.ANY_30,
    },
  },
  // TODO: Gilder (provider 143) has no open appointment data
  [NiraProviderEhrIds.GILDER]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_15,
    },
  },
  [NiraProviderEhrIds.DASARI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_15,
    },
  },
  [NiraProviderEhrIds.LACKEY]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.INITIAL_CONSULTATION]: OpenAppointmentTypeId.ANY_15,
      [AppointmentType.FOLLOW_UP]: OpenAppointmentTypeId.ANY_15,
    },
  },
};

const BOOKED_APPOINTMENT_TYPE_MAPPING: Record<string, ProviderAppointmentTypeMapping> = {
  [NiraProviderEhrIds.COUCH]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_15,
      [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_30,
    },
  },
  [NiraProviderEhrIds.HORVIT]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_15,
    },
  },
  [NiraProviderEhrIds.PECKHAM]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_40,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_40,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_20,
      [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_20,
    },
  },
  [NiraProviderEhrIds.KHOSRAVI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_30,
      [AppointmentType.TELEHEALTH]: BookedAppointmentTypeId.FU_TELEHEALTH_30,
    },
  },
  [NiraProviderEhrIds.PURDIE]: {
    appointmentTypeIds: {
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_30,
      [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_30,
      [AppointmentType.PROCEDURE]: BookedAppointmentTypeId.PROG_DBS_30,
    },
  },
  [NiraProviderEhrIds.SAINI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_30,
      [AppointmentType.INJECTION]: BookedAppointmentTypeId.INJ_BOTOX_30,
    },
  },
  [NiraProviderEhrIds.GILDER]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_30,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_15,
    },
  },
  [NiraProviderEhrIds.DASARI]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_30,
    },
  },
  [NiraProviderEhrIds.LACKEY]: {
    appointmentTypeIds: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.INITIAL_CONSULTATION]: BookedAppointmentTypeId.NEW_PATIENT_60,
      [AppointmentType.FOLLOW_UP]: BookedAppointmentTypeId.FOLLOW_UP_30,
    },
  },
};

// Some booked Athena IDs map to multiple internal appointment types on the
// forward path. Keep the reverse mapping explicit so scheduled appointments
// resolve to one canonical internal type deterministically.
const BOOKED_APPOINTMENT_TYPE_REVERSE_MAPPING: Record<string, ProviderBookedAppointmentTypeReverseMapping> = {
  [NiraProviderEhrIds.COUCH]: {
    [BookedAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
    [BookedAppointmentTypeId.INJ_BOTOX_30]: AppointmentType.INJECTION,
  },
  [NiraProviderEhrIds.HORVIT]: {
    [BookedAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
  },
  [NiraProviderEhrIds.PECKHAM]: {
    [BookedAppointmentTypeId.RESEARCH_15]: AppointmentType.RESEARCH,
    [BookedAppointmentTypeId.NEW_PATIENT_40]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_20]: AppointmentType.FOLLOW_UP,
    [BookedAppointmentTypeId.INJ_BOTOX_20]: AppointmentType.INJECTION,
  },
  [NiraProviderEhrIds.KHOSRAVI]: {
    [BookedAppointmentTypeId.NEW_PATIENT_60]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
    [BookedAppointmentTypeId.FU_TELEHEALTH_30]: AppointmentType.TELEHEALTH,
  },
  [NiraProviderEhrIds.PURDIE]: {
    [BookedAppointmentTypeId.FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
    [BookedAppointmentTypeId.INJ_BOTOX_30]: AppointmentType.INJECTION,
    [BookedAppointmentTypeId.PROG_DBS_30]: AppointmentType.PROCEDURE,
  },
  [NiraProviderEhrIds.SAINI]: {
    [BookedAppointmentTypeId.NEW_PATIENT_60]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
    [BookedAppointmentTypeId.INJ_BOTOX_30]: AppointmentType.INJECTION,
  },
  [NiraProviderEhrIds.GILDER]: {
    [BookedAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
  },
  [NiraProviderEhrIds.DASARI]: {
    [BookedAppointmentTypeId.NEW_PATIENT_60]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
  },
  [NiraProviderEhrIds.LACKEY]: {
    [BookedAppointmentTypeId.NEW_PATIENT_60]: AppointmentType.NEW_PATIENT_CHECKIN,
    [BookedAppointmentTypeId.FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
  },
};

function getNiraMapping(
  providerEhrId: string,
  mapping: Record<string, ProviderAppointmentTypeMapping>
): ProviderAppointmentTypeMapping {
  const providerMapping = mapping[providerEhrId];

  if (!providerMapping) {
    logAndThrow("No appointment type mapping for NIRA provider EHR ID", { providerEhrId });
  }

  return providerMapping;
}

export function getNiraOpenAppointmentTypeId(providerEhrId: string, appointmentType: AppointmentType): number {
  const mapping = getNiraMapping(providerEhrId, OPEN_APPOINTMENT_TYPE_MAPPING);

  const typeId = mapping.appointmentTypeIds[appointmentType] ?? mapping.defaultAppointmentTypeId;

  if (typeId === undefined) {
    logAndThrow("No appointment type mapping for provider and appointment type", { providerEhrId, appointmentType });
  }

  return typeId;
}

export function getNiraBookedAppointmentTypeId(providerEhrId: string, appointmentType: AppointmentType): number {
  const mapping = getNiraMapping(providerEhrId, BOOKED_APPOINTMENT_TYPE_MAPPING);

  const typeId = mapping.appointmentTypeIds[appointmentType] ?? mapping.defaultAppointmentTypeId;

  if (typeId === undefined) {
    logAndThrow("No appointment type mapping for provider and appointment type", { providerEhrId, appointmentType });
  }

  return typeId;
}

export function getNiraAppointmentTypeFromBookedEhrId(
  providerEhrId: string,
  athenaAppointmentTypeId: number
): AppointmentType | null {
  const providerMapping = BOOKED_APPOINTMENT_TYPE_REVERSE_MAPPING[providerEhrId];

  if (!providerMapping) {
    return null;
  }

  return providerMapping[athenaAppointmentTypeId as BookedAppointmentTypeId] ?? null;
}

type ProviderSlotPostProcessEntry = Partial<Record<AppointmentType, SlotPostProcessOption>>;

const postProcessingConfig: Partial<Record<NiraProviderEhrIds, ProviderSlotPostProcessEntry>> = {
  [NiraProviderEhrIds.SAINI]: {
    [AppointmentType.INITIAL_CONSULTATION]: SlotPostProcessOption.SlotMerge,
  },
};

export function getNiraSlotPostProcessingOption(
  providerEhrId: string,
  internalAppointmentType: AppointmentType
): SlotPostProcessOption | undefined {
  const providerAppointmentTypePostProcessOptions = postProcessingConfig[providerEhrId as NiraProviderEhrIds];

  return providerAppointmentTypePostProcessOptions?.[internalAppointmentType];
}

export const NIRA_AUTO_INTAKE_CONFIG: AutoIntakeConfig = {
  byClassification: {
    [Classification.COVER_PAGE]: { rate: 0.9 },
    [Classification.INTAKE]: { rate: 0.9 },
    [Classification.MEDICAL_HISTORY]: { rate: 0.9 },
    [Classification.PHARMACY_RELATED]: { rate: 0.9 },
    [Classification.PRIOR_AUTHORIZATION]: { rate: 0.9 },
    [Classification.REFERRAL_STATUS_REQUEST]: { rate: 0.9 },
  },
};
